import { createContext, useContext, useEffect } from 'react';
import { createForm, FormSignUpValues } from './FormSignUp';
import {
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetError,
  UseFormSetFocus,
  UseFormSetValue,
  UseFormTrigger,
  UseFormUnregister,
  UseFormResetField,
  UseFormWatch,
  UseFormGetFieldState,
  FieldErrors
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  clearSpecialCharactersFromNumber,
  formatDateToSend,
  pushGtmEvent
} from '../../utils/GenericFunctions';
import { createUser } from '../../services/user';
import { sendSocialData } from '../../services/socialNetWorkController';
import { ICreateUserRequest } from '../../interfaces/user/ICreateUser';
import { useControllerFlowStepsContext } from '../../hooks/ControllerFlowStepsContext';
import { setRefreshTokenSession, setTokenSession } from '../../utils/auth';
import { useAuthContext } from '../../hooks/AuthContext';
import { usePersistedForm, usePersistedState } from '../../hooks/PreserveState';

export interface SignUpContextData {
  register: UseFormRegister<FormSignUpValues>;
  handleSubmit: UseFormHandleSubmit<FormSignUpValues>;
  errors: FieldErrors<FormSignUpValues>;
  touchedFields: Partial<Readonly<any>>;
  trigger: UseFormTrigger<FormSignUpValues>;
  originDomainForm: string | null;
  submitForm(payload: any, callBack: any, provider?: string): void;
  watch: UseFormWatch<FormSignUpValues>;
  getValues: UseFormGetValues<FormSignUpValues>;
  setValue: UseFormSetValue<FormSignUpValues>;
  setFocus: UseFormSetFocus<FormSignUpValues>;
  setError: UseFormSetError<FormSignUpValues>;
  isValid: boolean;
  unregister: UseFormUnregister<FormSignUpValues>;
  resetField: UseFormResetField<FormSignUpValues>;
  getFieldState: UseFormGetFieldState<FormSignUpValues>;
}

export const SignUpContext = createContext({} as SignUpContextData);

const generateKey = (key: string) => `signup-${key}`;

const SignUpProvider = ({ children }: any) => {
  const [originDomainForm, setOrigin] = usePersistedState<any>(
    generateKey('originDomainForm'),
    ''
  );
  // create Form Fields
  const formFields = createForm();
  const {
    register,
    unregister,
    trigger,
    watch,
    handleSubmit,
    getValues,
    setValue,
    setFocus,
    setError,
    resetField,
    getFieldState,
    formState: { errors, touchedFields, isValid }
  } = usePersistedForm<FormSignUpValues | any>({
    storageKey: 'signup-form',
    defaultValues: {
      isSocial: false
    },
    formOptions: {
      mode: 'onChange',
      resolver: yupResolver(formFields, { abortEarly: false })
    }
  });

  const { socialData, tokenSocial } = useControllerFlowStepsContext();
  const { saveUserTokens } = useAuthContext();

  useEffect(() => {
    const regexOrigin = /videos/g;
    const regexAppOrigin = /sbt/g;
    if (regexOrigin.test(window.location.pathname)) {
      setOrigin('videos/signup');
    } else if (regexAppOrigin.test(window.location.pathname)) {
      setOrigin('sbt/signup');
    } else {
      setOrigin('signup');
    }
  }, []);

  useEffect(() => {
    if (socialData?.userName) {
      setValue('userName', socialData.userName, { shouldTouch: true });

      const [firstName, ...rest] = socialData.userName.split(' ');
      const lastName = rest.join(' ');
      setValue('firstName', firstName, { shouldTouch: true });
      setValue('lastName', lastName, { shouldTouch: true });

      trigger(['userName']);
      setValue('password', '');
    }

    if (socialData?.birthdate) {
      setValue('birthDate', socialData.birthdate, { shouldTouch: true });
      trigger(['birthDate']);
    }
  }, [socialData?.userName, socialData?.birthdate]);

  async function submitForm(formValues: any, callBack: any, provider?: string) {
    const valuesSubmit: ICreateUserRequest = {
      userName: formValues.userName?.trim(),
      acceptTermsOfUse: formValues.acceptTermsOfUse,
      birthDate: formatDateToSend(formValues.birthDate),

      email: formValues.email || undefined,
      emailCode: formValues.emailCode || undefined,

      cpf: clearSpecialCharactersFromNumber(formValues.cpf) || undefined,
      cellPhoneNumber:
        clearSpecialCharactersFromNumber(formValues.cellPhoneNumber) ||
        undefined,

      socialName: formValues?.socialName?.trim() || '',
      prefersSocial: formValues?.prefersSocial || false,
      picture: socialData?.picture
    };

    if (!provider) {
      valuesSubmit['password'] = formValues.password;
    } else {
      valuesSubmit['provider'] = provider;
      valuesSubmit['providerToken'] = tokenSocial;
    }

    try {
      const response = await createUser(valuesSubmit);

      if (provider) await sendSocialData(tokenSocial, provider);

      if (response?.accessToken) {
        saveUserTokens(response);
        setTokenSession(response.accessToken);
        setRefreshTokenSession(response.refreshToken);
        pushGtmEvent('cadastro_finalizado', {
          group: formValues.cpf ? 'com_cpf' : 'sem_cpf'
        });
      }
      callBack();
    } catch (error) {
      console.log('submitform error', error);
    }
  }

  return (
    <SignUpContext.Provider
      value={{
        originDomainForm,
        register,
        unregister,
        handleSubmit,
        errors,
        submitForm,
        touchedFields,
        trigger,
        getValues,
        setValue,
        setFocus,
        watch,
        setError,
        isValid,
        resetField,
        getFieldState
      }}
    >
      {children}
    </SignUpContext.Provider>
  );
};
export const useSignUpContext = () =>
  useContext<SignUpContextData>(SignUpContext);
export default SignUpProvider;
